import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Phone,
  Plus,
  Edit,
  Trash2,
  Volume2,
  MessageSquare,
  Building2,
  Settings,
  Play,
  RefreshCw,
  Check,
  X,
  Bot,
  Clock,
  PhoneIncoming,
  PhoneOutgoing,
  TestTube,
  Eye,
  FileText,
  Upload,
  Brain,
  File,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { API_BASE_URL } from '../config';
import LoadingSpinner from '../components/LoadingSpinner';
import clsx from 'clsx';
import toast from 'react-hot-toast';

const PIPELINE_OPTIONS = [
  { value: 'layer1', label: 'Layer 1 (OpenAI Realtime)', description: 'Low latency, OpenAI voices' },
  { value: 'layer4', label: 'Layer 4 (Realtime + ElevenLabs)', description: 'Best transcription + ethnic voices' },
  { value: 'retell', label: 'Retell AI', description: 'Enterprise-grade voice AI with outbound calling' }
];

const VOICE_OPTIONS = {
  layer1: [
    { value: 'alloy', label: 'Alloy' },
    { value: 'echo', label: 'Echo' },
    { value: 'fable', label: 'Fable' },
    { value: 'onyx', label: 'Onyx' },
    { value: 'nova', label: 'Nova' },
    { value: 'shimmer', label: 'Shimmer' }
  ],
  layer4: [
    { value: '9wYX8b0wRvLUEYtGuzP5', label: 'KeKe (Black Woman)' },
    { value: 'MEiBY6lrwud0dREUa5FQ', label: 'Brian (African American Radio Jockey)' },
    { value: 'YjlcD3XHztjJEo2wNszv', label: 'Tyre G (African American Male)' },
    { value: 'CwhRBWXzGAHq8TQ4Fs17', label: 'Roger (Laid-Back Male)' },
    { value: 'EXAVITQu4vr4xnSDxMaL', label: 'Sarah (Mature Female)' },
    { value: 'Ogc84JIyxBeL4VxfwZTd', label: 'Elijah May' }
  ],
  retell: [
    { value: '11labs-Adrian', label: 'Adrian (Male)' },
    { value: '11labs-Rachel', label: 'Rachel (Female)' },
    { value: '11labs-Drew', label: 'Drew (Male)' },
    { value: '11labs-Clyde', label: 'Clyde (Male)' },
    { value: '11labs-Paul', label: 'Paul (Male)' },
    { value: '11labs-Domi', label: 'Domi (Female)' },
    { value: '11labs-Dave', label: 'Dave (Male)' },
    { value: '11labs-Fin', label: 'Fin (Male)' },
    { value: '11labs-Sarah', label: 'Sarah (Female)' },
    { value: '11labs-Antoni', label: 'Antoni (Male)' },
    { value: '11labs-Thomas', label: 'Thomas (Male)' },
    { value: '11labs-Charlie', label: 'Charlie (Male)' },
    { value: '11labs-Emily', label: 'Emily (Female)' },
    { value: '11labs-Elli', label: 'Elli (Female)' },
    { value: '11labs-Callum', label: 'Callum (Male)' }
  ]
};

const DEFAULT_VOICE = {
  layer1: 'alloy',
  layer4: '9wYX8b0wRvLUEYtGuzP5',
  retell: '11labs-Adrian'
};

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'casual', label: 'Casual' },
  { value: 'enthusiastic', label: 'Enthusiastic' },
  { value: 'formal', label: 'Formal' }
];

const PERSONA_TYPES = [
  { value: 'customer', label: 'Regular Customer' },
  { value: 'prospect', label: 'New Prospect' },
  { value: 'angry_customer', label: 'Angry Customer' },
  { value: 'skeptical', label: 'Skeptical Buyer' },
  { value: 'busy', label: 'Busy/Impatient Person' },
  { value: 'technical', label: 'Technical/Detailed' },
  { value: 'price_sensitive', label: 'Price-Sensitive' },
  { value: 'friendly', label: 'Friendly/Easy' }
];

// Psychological profile document types
const PROFILE_DOC_TYPES = [
  { value: 'psychological_profile', label: 'Psychological Profile', description: 'MBTI, Big Five traits, core values, communication style' },
  { value: 'interview_transcript', label: 'Interview Transcript', description: 'Transcripts from interviews, podcasts, conversations' },
  { value: 'linkedin_data', label: 'LinkedIn/Professional Data', description: 'Professional background, experience, skills' },
  { value: 'context', label: 'Additional Context', description: 'Company info, current projects, recent news' },
  { value: 'personality_notes', label: 'Personality Notes', description: 'Observations about behavior, preferences, quirks' },
  { value: 'voice_samples', label: 'Voice/Communication Samples', description: 'Examples of how the person speaks or writes' },
  { value: 'other', label: 'Other', description: 'Any other relevant information' }
];

const QATestSuitesPage = () => {
  const navigate = useNavigate();
  const [suites, setSuites] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingSuite, setEditingSuite] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [runningTest, setRunningTest] = useState(null);

  // Psychological profile documents state
  const [profileDocs, setProfileDocs] = useState([]);
  const [showProfileSection, setShowProfileSection] = useState(false);
  const [showProfileDocModal, setShowProfileDocModal] = useState(false);
  const [editingProfileDoc, setEditingProfileDoc] = useState(null);
  const [profileDocForm, setProfileDocForm] = useState({
    name: '',
    description: '',
    documentType: 'psychological_profile',
    content: '',
    includeInPrompt: true,
    priority: 0
  });

  const [form, setForm] = useState({
    name: '',
    description: '',
    targetType: 'inbound',
    targetNumber: '', // For outbound: the number to call
    // AI Configuration (like Demo Area)
    phoneNumber: '', // For inbound: the number humans call
    businessName: '',
    businessContext: '',
    aiGreeting: '',
    aiInstructions: '',
    aiPersonality: '',
    aiTone: 'professional',
    voiceModel: 'alloy',
    pipeline: 'layer1',
    language: 'en-US',
    // Scheduling
    scheduleEnabled: false,
    scheduleIntervalMins: 5,
    scheduleMaxCalls: 10,
    // Call behavior mode
    callBehaviorMode: 'single', // 'single' = same behavior, 'sequential' = different per call
    // Call scenario simulation
    simulateHold: false,
    holdDurationSeconds: 15,
    simulateIvrMenu: false,
    ivrMenuPrompt: '',
    simulateAnswerDelay: false,
    answerDelaySeconds: 10
  });

  // Call behaviors for sequential mode
  const [callBehaviors, setCallBehaviors] = useState([]);
  const [showBehaviorForm, setShowBehaviorForm] = useState(false);
  const [editingBehaviorIndex, setEditingBehaviorIndex] = useState(null);
  const [behaviorForm, setBehaviorForm] = useState({
    callNumber: 1,
    name: '',
    personaType: 'customer',
    instructions: '',
    questionsToAsk: ['']
  });

  useEffect(() => {
    loadSuites();
    loadPhoneNumbers();
  }, []);

  const loadSuites = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/qa/suites`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSuites(data.suites || []);
      }
    } catch (error) {
      console.error('Failed to load suites:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPhoneNumbers = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/phone/my-numbers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPhoneNumbers(data.numbers || data.data || []);
      }
    } catch (error) {
      console.error('Failed to load phone numbers:', error);
    }
  };

  // Psychological profile document functions
  const loadProfileDocs = async (suiteId) => {
    if (!suiteId) return;
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/qa/suites/${suiteId}/profile-docs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setProfileDocs(data.docs || []);
      }
    } catch (error) {
      console.error('Failed to load profile docs:', error);
    }
  };

  const resetProfileDocForm = () => {
    setProfileDocForm({
      name: '',
      description: '',
      documentType: 'psychological_profile',
      content: '',
      includeInPrompt: true,
      priority: 0
    });
    setEditingProfileDoc(null);
  };

  const handleProfileDocSubmit = async (e) => {
    e.preventDefault();
    if (!editingSuite) return;

    try {
      const token = localStorage.getItem('authToken');
      const url = editingProfileDoc
        ? `${API_BASE_URL}/api/qa/profile-docs/${editingProfileDoc.id}`
        : `${API_BASE_URL}/api/qa/suites/${editingSuite.id}/profile-docs`;

      const response = await fetch(url, {
        method: editingProfileDoc ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profileDocForm)
      });

      if (response.ok) {
        setShowProfileDocModal(false);
        resetProfileDocForm();
        loadProfileDocs(editingSuite.id);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to save profile document');
      }
    } catch (error) {
      console.error('Failed to save profile doc:', error);
      toast.error('Failed to save profile document');
    }
  };

  const handleDeleteProfileDoc = async (docId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/qa/profile-docs/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        loadProfileDocs(editingSuite.id);
      }
    } catch (error) {
      console.error('Failed to delete profile doc:', error);
    }
  };

  const openEditProfileDoc = (doc) => {
    setEditingProfileDoc(doc);
    setProfileDocForm({
      name: doc.name || '',
      description: doc.description || '',
      documentType: doc.documentType || 'psychological_profile',
      content: doc.content || '',
      includeInPrompt: doc.includeInPrompt !== false,
      priority: doc.priority || 0
    });
    setShowProfileDocModal(true);
  };

  const getDocTypeLabel = (type) => {
    const docType = PROFILE_DOC_TYPES.find(d => d.value === type);
    return docType ? docType.label : type;
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      targetType: 'inbound',
      targetNumber: '',
      phoneNumber: '',
      businessName: '',
      businessContext: '',
      aiGreeting: '',
      aiInstructions: '',
      aiPersonality: '',
      aiTone: 'professional',
      voiceModel: 'alloy',
      pipeline: 'layer1',
      language: 'en-US',
      scheduleEnabled: false,
      scheduleIntervalMins: 5,
      scheduleMaxCalls: 10,
      callBehaviorMode: 'single',
      // Call scenario simulation
      simulateHold: false,
      holdDurationSeconds: 15,
      simulateIvrMenu: false,
      ivrMenuPrompt: '',
      simulateAnswerDelay: false,
      answerDelaySeconds: 10
    });
    setCallBehaviors([]);
    setProfileDocs([]);
    setShowProfileSection(false);
    resetProfileDocForm();
    setEditingSuite(null);
  };

  const openEditModal = (suite) => {
    setEditingSuite(suite);
    setForm({
      name: suite.name || '',
      description: suite.description || '',
      targetType: suite.targetType || 'inbound',
      targetNumber: suite.targetNumber || '',
      phoneNumber: suite.phoneNumber || '',
      businessName: suite.businessName || '',
      businessContext: suite.businessContext || '',
      aiGreeting: suite.aiGreeting || '',
      aiInstructions: suite.aiInstructions || '',
      aiPersonality: suite.aiPersonality || '',
      aiTone: suite.aiTone || 'professional',
      voiceModel: suite.voiceModel || 'alloy',
      pipeline: suite.pipeline || 'layer1',
      language: suite.language || 'en-US',
      scheduleEnabled: suite.scheduleEnabled || false,
      scheduleIntervalMins: suite.scheduleIntervalMins || 5,
      scheduleMaxCalls: suite.scheduleMaxCalls || 10,
      callBehaviorMode: suite.callBehaviorMode || 'single',
      // Call scenario simulation
      simulateHold: suite.simulateHold || false,
      holdDurationSeconds: suite.holdDurationSeconds || 15,
      simulateIvrMenu: suite.simulateIvrMenu || false,
      ivrMenuPrompt: suite.ivrMenuPrompt || '',
      simulateAnswerDelay: suite.simulateAnswerDelay || false,
      answerDelaySeconds: suite.answerDelaySeconds || 10
    });
    // Load call behaviors if they exist
    setCallBehaviors(suite.callBehaviors || []);
    // Load psychological profile documents
    loadProfileDocs(suite.id);
    setShowProfileSection(false); // Collapse by default
    setShowModal(true);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      const url = editingSuite
        ? `${API_BASE_URL}/api/qa/suites/${editingSuite.id}`
        : `${API_BASE_URL}/api/qa/suites`;

      // Include call behaviors if in sequential mode
      const payload = {
        ...form,
        callBehaviors: form.callBehaviorMode === 'sequential' ? callBehaviors : []
      };

      const response = await fetch(url, {
        method: editingSuite ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setShowModal(false);
        resetForm();
        loadSuites();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to save test suite');
      }
    } catch (error) {
      console.error('Failed to save suite:', error);
      toast.error('Failed to save test suite');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/qa/suites/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDeleteConfirm(null);
        loadSuites();
      }
    } catch (error) {
      console.error('Failed to delete suite:', error);
      toast.error('Failed to delete test suite');
    } finally {
      setLoading(false);
    }
  };

  const runTestSuite = async (suiteId) => {
    try {
      setRunningTest(suiteId);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/qa/suites/${suiteId}/run`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        navigate(`/app/qa/runs/${data.testRun.id}`);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to start test run');
      }
    } catch (error) {
      console.error('Failed to run test:', error);
      toast.error('Failed to start test run');
    } finally {
      setRunningTest(null);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return phone;
  };

  const getPipelineLabel = (value) => {
    const opt = PIPELINE_OPTIONS.find(o => o.value === value);
    return opt ? opt.label : value;
  };

  const getVoiceLabel = (voiceId, pipeline) => {
    const voices = VOICE_OPTIONS[pipeline] || VOICE_OPTIONS.layer1;
    const opt = voices.find(o => o.value === voiceId);
    return opt ? opt.label : voiceId;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">QA Testing Areas</h1>
          <p className="text-sm text-gray-500 mt-1">
            Configure AI test areas that can receive and make calls for quality assurance
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadSuites}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" />
            New Test Area
          </button>
        </div>
      </div>

      {/* Suite Cards */}
      {loading && suites.length === 0 ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : suites.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <TestTube className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No QA Testing Areas</h3>
          <p className="text-gray-500 mb-4">
            Create a test area to configure an AI for quality assurance testing
          </p>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" />
            Create Test Area
          </button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {suites.map((suite) => (
            <div
              key={suite.id}
              className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{suite.name}</h3>
                  {suite.phoneNumber && (
                    <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                      <Phone className="w-3 h-3" />
                      {formatPhoneNumber(suite.phoneNumber)}
                    </p>
                  )}
                </div>
                <span className={clsx(
                  'px-2 py-1 text-xs rounded-full',
                  suite.isActive
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-600'
                )}>
                  {suite.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="space-y-2 text-sm text-gray-600 mb-4">
                {suite.businessName && (
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    <span>{suite.businessName}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  <span>{getPipelineLabel(suite.pipeline)}</span>
                </div>
                {suite.voiceModel && (
                  <div className="flex items-center gap-2">
                    <Volume2 className="w-4 h-4" />
                    <span>{getVoiceLabel(suite.voiceModel, suite.pipeline)}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {suite.targetType === 'inbound' ? (
                    <PhoneIncoming className="w-4 h-4" />
                  ) : (
                    <PhoneOutgoing className="w-4 h-4" />
                  )}
                  <span className="capitalize">{suite.targetType} testing</span>
                </div>
              </div>

              {/* AI Instructions Preview */}
              {suite.aiInstructions && (
                <div className="mb-4 p-2 bg-blue-50 rounded text-xs text-blue-700">
                  <Bot className="w-3 h-3 inline mr-1" />
                  AI configured with custom instructions
                </div>
              )}

              <div className="flex gap-2 border-t border-gray-100 pt-3">
                <button
                  onClick={() => openEditModal(suite)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-gray-600 bg-gray-50 rounded hover:bg-gray-100"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
                <Link
                  to={`/app/qa/suites/${suite.id}`}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
                >
                  <Eye className="w-4 h-4" />
                  View
                </Link>
                <button
                  onClick={() => setDeleteConfirm(suite.id)}
                  className="flex items-center justify-center gap-1 px-3 py-2 text-sm text-red-600 bg-red-50 rounded hover:bg-red-100"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit}>
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold">
                  {editingSuite ? 'Edit QA Test Area' : 'Create QA Test Area'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Configure the AI that will handle calls for this test area
                </p>
              </div>

              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Test Area Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="e.g., NexusFlow Investor Demo"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number (for inbound calls)
                    </label>
                    <select
                      value={form.phoneNumber}
                      onChange={(e) => setForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select a phone number</option>
                      {phoneNumbers.map((num) => (
                        <option key={num.id} value={num.phoneNumber}>
                          {formatPhoneNumber(num.phoneNumber)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={form.description}
                    onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="Brief description of this test area..."
                  />
                </div>

                {/* Call Direction */}
                <div className="border-t border-gray-100 pt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Call Direction</h3>
                  <div className="grid gap-3 md:grid-cols-2">
                    <label
                      className={clsx(
                        'flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors',
                        form.targetType === 'inbound'
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <input
                        type="radio"
                        name="targetType"
                        value="inbound"
                        checked={form.targetType === 'inbound'}
                        onChange={(e) => setForm(prev => ({ ...prev, targetType: e.target.value }))}
                        className="mt-1"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <PhoneIncoming className="w-4 h-4 text-green-600" />
                          <span className="font-medium text-gray-900">Inbound Testing</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Humans call IN to this number and talk to the AI. The AI uses the persona/instructions you configure below.
                        </p>
                      </div>
                    </label>
                    <label
                      className={clsx(
                        'flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors',
                        form.targetType === 'outbound'
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <input
                        type="radio"
                        name="targetType"
                        value="outbound"
                        checked={form.targetType === 'outbound'}
                        onChange={(e) => setForm(prev => ({ ...prev, targetType: e.target.value }))}
                        className="mt-1"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <PhoneOutgoing className="w-4 h-4 text-blue-600" />
                          <span className="font-medium text-gray-900">Outbound Testing</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          The AI calls OUT to test another system. Configure what number to call and how the tester AI should behave.
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Target Number for Outbound */}
                {form.targetType === 'outbound' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Target Number to Call *
                    </label>
                    <input
                      type="text"
                      required={form.targetType === 'outbound'}
                      value={form.targetNumber || ''}
                      onChange={(e) => setForm(prev => ({ ...prev, targetNumber: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="+18325551234"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      The phone number the AI tester will call to test
                    </p>
                  </div>
                )}

                {/* Pipeline & Voice */}
                <div className="border-t border-gray-100 pt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Pipeline & Voice</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pipeline
                      </label>
                      <select
                        value={form.pipeline}
                        onChange={(e) => {
                          const newPipeline = e.target.value;
                          setForm(prev => ({
                            ...prev,
                            pipeline: newPipeline,
                            voiceModel: DEFAULT_VOICE[newPipeline]
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      >
                        {PIPELINE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Voice Model
                      </label>
                      <select
                        value={form.voiceModel}
                        onChange={(e) => setForm(prev => ({ ...prev, voiceModel: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      >
                        {(VOICE_OPTIONS[form.pipeline] || VOICE_OPTIONS.layer1).map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* AI Identity */}
                <div className="border-t border-gray-100 pt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Bot className="w-4 h-4" />
                    {form.targetType === 'inbound'
                      ? 'AI Identity & Persona (How the AI responds to callers)'
                      : 'Tester AI Behavior (How the AI tester acts when calling)'
                    }
                  </h3>
                  <div className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Business/Persona Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={form.businessName}
                          onChange={(e) => setForm(prev => ({ ...prev, businessName: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                          placeholder="e.g., NexusFlow, Brian's HVAC"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          AI Tone
                        </label>
                        <select
                          value={form.aiTone}
                          onChange={(e) => setForm(prev => ({ ...prev, aiTone: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        >
                          {TONE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        AI Greeting
                      </label>
                      <input
                        type="text"
                        value={form.aiGreeting}
                        onChange={(e) => setForm(prev => ({ ...prev, aiGreeting: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        placeholder="Hi, thanks for calling! I'm Marcus, founder of NexusFlow. How can I help you?"
                      />
                      <p className="text-xs text-gray-500 mt-1">What the AI says when answering</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {form.targetType === 'inbound' ? 'AI Instructions / Persona *' : 'Tester AI Instructions *'}
                      </label>
                      <textarea
                        required
                        rows={10}
                        value={form.aiInstructions}
                        onChange={(e) => setForm(prev => ({ ...prev, aiInstructions: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                        placeholder={form.targetType === 'inbound'
                          ? `You are Marcus Chen, the 32-year-old founder and CEO of NexusFlow...

Describe the full AI persona, background, knowledge, and how it should behave in conversations.`
                          : `You are a potential customer calling to inquire about services...

Describe how the AI tester should behave: what persona to adopt, what questions to ask, what objections to raise.`
                        }
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {form.targetType === 'inbound'
                          ? 'The complete instructions for how the AI should behave, its persona, knowledge, and conversation style'
                          : 'Instructions for how the AI tester should act when calling the target number'
                        }
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Additional Context / Knowledge Base
                      </label>
                      <textarea
                        rows={4}
                        value={form.businessContext}
                        onChange={(e) => setForm(prev => ({ ...prev, businessContext: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        placeholder="Key facts, data, pricing, FAQs, or other information the AI should know..."
                      />
                    </div>
                  </div>
                </div>

                {/* Call Frequency & Scheduling */}
                <div className="border-t border-gray-100 pt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {form.targetType === 'outbound' ? 'Outbound Call Schedule' : 'Test Call Settings'}
                  </h3>

                  {form.targetType === 'outbound' && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="scheduleEnabled"
                          checked={form.scheduleEnabled}
                          onChange={(e) => setForm(prev => ({ ...prev, scheduleEnabled: e.target.checked }))}
                          className="rounded border-gray-300 text-indigo-600"
                        />
                        <label htmlFor="scheduleEnabled" className="text-sm text-gray-700">
                          Enable automated scheduled calls
                        </label>
                      </div>

                      {form.scheduleEnabled && (
                        <div className="grid gap-4 md:grid-cols-2 pl-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Call Interval (minutes)
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={form.scheduleIntervalMins}
                              onChange={(e) => setForm(prev => ({ ...prev, scheduleIntervalMins: parseInt(e.target.value) || 5 }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                            <p className="text-xs text-gray-500 mt-1">Make a call every X minutes</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Maximum Calls
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={form.scheduleMaxCalls}
                              onChange={(e) => setForm(prev => ({ ...prev, scheduleMaxCalls: parseInt(e.target.value) || 10 }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                            <p className="text-xs text-gray-500 mt-1">Stop after this many total calls</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Call Behavior Variation */}
                <div className="border-t border-gray-100 pt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">
                    Call Behavior Variation
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Should each call behave the same, or should different calls test different scenarios?
                  </p>

                  <div className="grid gap-3 md:grid-cols-2 mb-4">
                    <label
                      className={clsx(
                        'flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors',
                        form.callBehaviorMode === 'single'
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <input
                        type="radio"
                        name="callBehaviorMode"
                        value="single"
                        checked={form.callBehaviorMode === 'single'}
                        onChange={(e) => setForm(prev => ({ ...prev, callBehaviorMode: e.target.value }))}
                        className="mt-1"
                      />
                      <div>
                        <span className="font-medium text-gray-900">Same Behavior</span>
                        <p className="text-sm text-gray-500 mt-1">
                          Every call uses the same AI instructions configured above
                        </p>
                      </div>
                    </label>
                    <label
                      className={clsx(
                        'flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors',
                        form.callBehaviorMode === 'sequential'
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <input
                        type="radio"
                        name="callBehaviorMode"
                        value="sequential"
                        checked={form.callBehaviorMode === 'sequential'}
                        onChange={(e) => setForm(prev => ({ ...prev, callBehaviorMode: e.target.value }))}
                        className="mt-1"
                      />
                      <div>
                        <span className="font-medium text-gray-900">Sequential Behaviors</span>
                        <p className="text-sm text-gray-500 mt-1">
                          Call 1 does X, Call 2 does Y, Call 3 does Z (cycle through)
                        </p>
                      </div>
                    </label>
                  </div>

                  {form.callBehaviorMode === 'sequential' && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-medium text-gray-700">
                          Call Behaviors ({callBehaviors.length} defined)
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setBehaviorForm({
                              callNumber: callBehaviors.length + 1,
                              name: '',
                              personaType: 'customer',
                              instructions: '',
                              questionsToAsk: ['']
                            });
                            setEditingBehaviorIndex(null);
                            setShowBehaviorForm(true);
                          }}
                          className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                        >
                          <Plus className="w-4 h-4" />
                          Add Behavior
                        </button>
                      </div>

                      {callBehaviors.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">
                          No behaviors defined. Add behaviors to specify different actions for each call.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {callBehaviors.map((behavior, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between bg-white p-3 rounded border border-gray-200"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-sm">
                                  {behavior.callNumber}
                                </div>
                                <div>
                                  <span className="font-medium text-gray-900">
                                    {behavior.name || `Call ${behavior.callNumber}`}
                                  </span>
                                  <p className="text-xs text-gray-500">
                                    {PERSONA_TYPES.find(p => p.value === behavior.personaType)?.label || behavior.personaType}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setBehaviorForm(behavior);
                                    setEditingBehaviorIndex(index);
                                    setShowBehaviorForm(true);
                                  }}
                                  className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCallBehaviors(prev => prev.filter((_, i) => i !== index));
                                  }}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Call Scenario Simulation */}
                <div className="border-t border-gray-100 pt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Call Scenario Simulation
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Simulate real-world call scenarios like hold times, IVR menus, and answer delays.
                    These override global QA config settings when enabled.
                  </p>

                  <div className="space-y-4">
                    {/* Simulate Answer Delay */}
                    <div className={clsx(
                      'rounded-lg border p-4',
                      form.simulateAnswerDelay ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50'
                    )}>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium text-gray-900">Simulate Answer Delay</span>
                          <p className="text-xs text-gray-500 mt-0.5">
                            AI stays silent for a set duration before greeting the caller
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.simulateAnswerDelay}
                            onChange={(e) => setForm(prev => ({ ...prev, simulateAnswerDelay: e.target.checked }))}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                        </label>
                      </div>
                      {form.simulateAnswerDelay && (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Delay Duration (seconds)
                          </label>
                          <input
                            type="number"
                            min="5"
                            max="60"
                            value={form.answerDelaySeconds}
                            onChange={(e) => setForm(prev => ({ ...prev, answerDelaySeconds: parseInt(e.target.value) || 10 }))}
                            className="w-32 px-3 py-2 border border-gray-300 rounded-lg"
                          />
                          <p className="text-xs text-gray-500 mt-1">5-60 seconds</p>
                        </div>
                      )}
                    </div>

                    {/* Simulate IVR Menu */}
                    <div className={clsx(
                      'rounded-lg border p-4',
                      form.simulateIvrMenu ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50'
                    )}>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium text-gray-900">Simulate IVR Menu</span>
                          <p className="text-xs text-gray-500 mt-0.5">
                            AI presents an automated phone menu before speaking as a human
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.simulateIvrMenu}
                            onChange={(e) => setForm(prev => ({ ...prev, simulateIvrMenu: e.target.checked }))}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                        </label>
                      </div>
                      {form.simulateIvrMenu && (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            IVR Menu Prompt
                          </label>
                          <textarea
                            rows={3}
                            value={form.ivrMenuPrompt}
                            onChange={(e) => setForm(prev => ({ ...prev, ivrMenuPrompt: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder="Press 1 for appointments, press 2 for billing, press 0 for operator"
                          />
                        </div>
                      )}
                    </div>

                    {/* Simulate Hold */}
                    <div className={clsx(
                      'rounded-lg border p-4',
                      form.simulateHold ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-gray-50'
                    )}>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium text-gray-900">Simulate Hold</span>
                          <p className="text-xs text-gray-500 mt-0.5">
                            AI puts the caller on hold mid-conversation for a set duration
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.simulateHold}
                            onChange={(e) => setForm(prev => ({ ...prev, simulateHold: e.target.checked }))}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                        </label>
                      </div>
                      {form.simulateHold && (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Hold Duration (seconds)
                          </label>
                          <input
                            type="number"
                            min="5"
                            max="120"
                            value={form.holdDurationSeconds}
                            onChange={(e) => setForm(prev => ({ ...prev, holdDurationSeconds: parseInt(e.target.value) || 15 }))}
                            className="w-32 px-3 py-2 border border-gray-300 rounded-lg"
                          />
                          <p className="text-xs text-gray-500 mt-1">5-120 seconds</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Psychological Profile Documents Section - Only show when editing existing suite */}
                {editingSuite && (
                  <div className="border-t border-gray-200 pt-6">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setShowProfileSection(!showProfileSection)}
                    >
                      <div className="flex items-center gap-2">
                        <Brain className="w-5 h-5 text-purple-600" />
                        <h3 className="text-lg font-medium text-gray-900">
                          Psychological Profile Documents
                        </h3>
                        <span className="text-sm text-gray-500">
                          ({profileDocs.length} documents)
                        </span>
                      </div>
                      <button type="button" className="text-gray-500 hover:text-gray-700">
                        {showProfileSection ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </button>
                    </div>

                    {showProfileSection && (
                      <div className="mt-4 space-y-4">
                        <p className="text-sm text-gray-500">
                          Upload psychological profiles, interview transcripts, and other documents to fine-tune the AI persona for this test area.
                        </p>

                        {/* Add New Document Button */}
                        <button
                          type="button"
                          onClick={() => {
                            resetProfileDocForm();
                            setShowProfileDocModal(true);
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 border border-purple-200"
                        >
                          <Plus className="w-4 h-4" />
                          Add Profile Document
                        </button>

                        {/* Document List */}
                        {profileDocs.length > 0 && (
                          <div className="space-y-2">
                            {profileDocs.map((doc) => (
                              <div
                                key={doc.id}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                              >
                                <div className="flex items-center gap-3">
                                  <FileText className="w-5 h-5 text-gray-400" />
                                  <div>
                                    <div className="font-medium text-gray-900">{doc.name}</div>
                                    <div className="text-xs text-gray-500 flex items-center gap-2">
                                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                                        {getDocTypeLabel(doc.documentType)}
                                      </span>
                                      {doc.includeInPrompt && (
                                        <span className="text-green-600">Included in AI prompt</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => openEditProfileDoc(doc)}
                                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteProfileDoc(doc.id)}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {profileDocs.length === 0 && (
                          <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                            <Brain className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">
                              No profile documents yet. Add documents to enhance the AI persona.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : (editingSuite ? 'Update Test Area' : 'Create Test Area')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Profile Document Modal */}
      {showProfileDocModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleProfileDocSubmit}>
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <Brain className="w-5 h-5 text-purple-600" />
                  {editingProfileDoc ? 'Edit Profile Document' : 'Add Profile Document'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Add psychological profile data to enhance the AI persona
                </p>
              </div>

              <div className="p-6 space-y-4">
                {/* Document Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Document Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={profileDocForm.name}
                    onChange={(e) => setProfileDocForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Elijah May - Psychological Profile"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                {/* Document Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Document Type *
                  </label>
                  <select
                    value={profileDocForm.documentType}
                    onChange={(e) => setProfileDocForm(prev => ({ ...prev, documentType: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    {PROFILE_DOC_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label} - {type.description}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={profileDocForm.description}
                    onChange={(e) => setProfileDocForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of what this document contains"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content *
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Paste psychological profile, interview transcript, or other relevant content. Markdown formatting is supported.
                  </p>
                  <textarea
                    required
                    value={profileDocForm.content}
                    onChange={(e) => setProfileDocForm(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Paste your psychological profile, interview transcript, or other content here..."
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm"
                  />
                </div>

                {/* Options */}
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={profileDocForm.includeInPrompt}
                      onChange={(e) => setProfileDocForm(prev => ({ ...prev, includeInPrompt: e.target.checked }))}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">Include in AI prompt</span>
                  </label>

                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-700">Priority:</label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      value={profileDocForm.priority}
                      onChange={(e) => setProfileDocForm(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                    <span className="text-xs text-gray-500">(Higher = more prominent)</span>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowProfileDocModal(false);
                    resetProfileDocForm();
                  }}
                  className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  {editingProfileDoc ? 'Update Document' : 'Add Document'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Behavior Form Modal */}
      {showBehaviorForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">
                {editingBehaviorIndex !== null ? 'Edit Call Behavior' : 'Add Call Behavior'}
              </h3>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid gap-4 grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Call Number
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={behaviorForm.callNumber}
                    onChange={(e) => setBehaviorForm(prev => ({ ...prev, callNumber: parseInt(e.target.value) || 1 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Persona Type
                  </label>
                  <select
                    value={behaviorForm.personaType}
                    onChange={(e) => setBehaviorForm(prev => ({ ...prev, personaType: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    {PERSONA_TYPES.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Behavior Name (optional)
                </label>
                <input
                  type="text"
                  value={behaviorForm.name}
                  onChange={(e) => setBehaviorForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Angry customer complaint call"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Specific Instructions for this Call
                </label>
                <textarea
                  rows={4}
                  value={behaviorForm.instructions}
                  onChange={(e) => setBehaviorForm(prev => ({ ...prev, instructions: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Be aggressive about wanting a refund. Interrupt frequently. Don't accept the first solution offered."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Questions to Ask
                </label>
                {behaviorForm.questionsToAsk.map((q, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={q}
                      onChange={(e) => {
                        const newQuestions = [...behaviorForm.questionsToAsk];
                        newQuestions[i] = e.target.value;
                        setBehaviorForm(prev => ({ ...prev, questionsToAsk: newQuestions }));
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder={`Question ${i + 1}`}
                    />
                    {behaviorForm.questionsToAsk.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          setBehaviorForm(prev => ({
                            ...prev,
                            questionsToAsk: prev.questionsToAsk.filter((_, idx) => idx !== i)
                          }));
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setBehaviorForm(prev => ({
                    ...prev,
                    questionsToAsk: [...prev.questionsToAsk, '']
                  }))}
                  className="text-sm text-indigo-600 hover:text-indigo-700"
                >
                  + Add another question
                </button>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowBehaviorForm(false)}
                className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (editingBehaviorIndex !== null) {
                    setCallBehaviors(prev => prev.map((b, i) =>
                      i === editingBehaviorIndex ? behaviorForm : b
                    ));
                  } else {
                    setCallBehaviors(prev => [...prev, behaviorForm]);
                  }
                  setShowBehaviorForm(false);
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                {editingBehaviorIndex !== null ? 'Update' : 'Add'} Behavior
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Test Area</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete this test area? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QATestSuitesPage;
